"use client";

import { useEffect } from "react";
import { useParams } from "next/navigation";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { useCookieConsent } from "@leabankab/component-library/cookie-consent/react";
import { useCountry } from "@/config/country-context";
import { useRuntimeConfig } from "@/config/runtime-config-context";
import { extractFlowIdFromUrl } from "@/core/auth/flow-id-from-request";
import { TEST_FLOW_ID } from "@/core/flow/test-flow-id";
import { DD_SERVICE } from "@/datadog/service";
import {
	shouldForwardBrowserLog,
	shouldForwardRumEvent,
} from "@/datadog/utils/retryable-network-error";
import { getViewId } from "@/datadog/view-id";

// Set from package.json (+ BUILD_VER) in next.config.mjs; must match the
// Dockerfile sourcemap `--release-version` for RUM error resolution.
const ddVersion = process.env.APP_VERSION ?? "0.0.0";

let initialised = false;

export default function DatadogInit() {
	const { flowId } = useParams();
	const { consent } = useCookieConsent();
	const { datadog } = useRuntimeConfig();
	const country = useCountry();
	const hasAnalyticsConsent = consent?.analytics === true;

	useEffect(() => {
		const id =
			(typeof flowId === "string" ? flowId : undefined) ??
			extractFlowIdFromUrl(window.location.href);

		if (!datadog || id === TEST_FLOW_ID) return;

		// RUM and the browser logs SDK set analytics cookies. Once started they
		// can't be torn down, so a withdrawn consent stops tracking instead.
		if (!hasAnalyticsConsent) {
			if (initialised) {
				datadogRum.setTrackingConsent("not-granted");
				datadogLogs.setTrackingConsent("not-granted");
			}
			return;
		}

		if (initialised) {
			datadogRum.setTrackingConsent("granted");
			datadogLogs.setTrackingConsent("granted");
		} else {
			datadogRum.init({
				applicationId: datadog.applicationId,
				clientToken: datadog.clientToken,
				site: "datadoghq.eu",
				service: DD_SERVICE,
				env: datadog.env,
				version: ddVersion,
				sessionSampleRate: 100,
				sessionReplaySampleRate: 0,
				trackUserInteractions: true,
				trackResources: true,
				trackLongTasks: true,
				defaultPrivacyLevel: "mask-user-input",
				trackingConsent: "granted",
				beforeSend: shouldForwardRumEvent,
			});

			datadogLogs.init({
				clientToken: datadog.clientToken,
				version: ddVersion,
				site: "datadoghq.eu",
				service: DD_SERVICE,
				forwardErrorsToLogs: true,
				beforeSend: shouldForwardBrowserLog,
				// Not "all": a stray console call carrying customer data would bypass the
				// redaction in `sendServerLog` and reach Datadog verbatim.
				forwardConsoleLogs: ["warn", "error"],
				sessionSampleRate: 100,
				trackingConsent: "granted",
			});
			initialised = true;
		}

		datadogLogs.setGlobalContextProperty("version", ddVersion);
		datadogLogs.setGlobalContextProperty("service", DD_SERVICE);
		datadogLogs.setGlobalContextProperty("country", country);

		datadogRum.setGlobalContextProperty("country", country);
		// The same id the relayed logs carry, so a tab's browser and server logs join.
		const viewId = getViewId();
		if (viewId) {
			datadogLogs.setGlobalContextProperty("view_id", viewId);
			datadogRum.setGlobalContextProperty("view_id", viewId);
		}
		// `flowId` is the only customer identifier we are allowed to send; it lets
		// a RUM session be joined to the server logs without carrying PII.
		if (id) {
			datadogLogs.setGlobalContextProperty("flow_id", id);
			datadogRum.setGlobalContextProperty("flow_id", id);
			datadogRum.setUser({ id });
		}
	}, [datadog, country, flowId, hasAnalyticsConsent]);

	return null;
}
